/**
 * Filter bar for the OpenSearch-backed entity pages (Individual Donors,
 * Voters; electinfo/sites#277).
 *
 * Each control maps to one of the entity's allowed API filters
 * (api.elect.info resolvers/opensearch-entities.ts filter specs):
 *
 *   select - one value from a fixed list
 *   checks - any of several values from a fixed list
 *   facet  - one value from the API's entityFacets (with counts), loaded
 *            once the controls it dependsOn have values (county by state)
 *   bands  - one min/max band (e.g. $10K+, ages 18-29)
 *   text   - a typed value (full-text or prefix, per the API filter)
 *   cycle  - the page's ?cycle= (the global cycle facet, sites#355): not an
 *            API filter; choosing one reloads the page in that cycle
 *
 * The state lives in the URL as f_<key>=v1|v2, so filtered views can be
 * shared and survive reloads.
 */

import { currentCycle, withCycle } from './cycle-facet';
import type { EntityFilterInput, FacetBucket, FilterBand, FilterControl } from './types';

const URL_PREFIX = 'f_';
const URL_SEPARATOR = '|';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export const FILTER_BAR_STYLES = `
  .entity-filter-bar {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
    gap: 10px 14px;
    margin: 14px 0 6px;
    align-items: end;
  }
  .entity-filter-control { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .entity-filter-control[hidden] { display: none; }
  .entity-filter-control > label, .entity-filter-control > span.entity-filter-label {
    font-size: 0.8em;
    font-weight: 600;
    color: var(--secondary, #2c5282);
  }
  .entity-filter-control select, .entity-filter-control input[type="text"], .entity-filter-control summary {
    padding: 7px 10px;
    font-size: 0.92em;
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 6px;
    background: white;
    font-family: inherit;
    min-width: 0;
  }
  .entity-filter-control details { position: relative; }
  .entity-filter-control summary { cursor: pointer; list-style: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .entity-filter-control summary::-webkit-details-marker { display: none; }
  .entity-filter-control .entity-filter-checks {
    position: absolute;
    z-index: 20;
    background: white;
    border: 1px solid var(--border, #e2e8f0);
    border-radius: 6px;
    padding: 8px 10px;
    margin-top: 4px;
    max-height: 280px;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    min-width: 100%;
  }
  .entity-filter-checks label { display: flex; gap: 6px; align-items: center; font-size: 0.9em; padding: 2px 0; white-space: nowrap; }
  .entity-filter-actions { display: flex; gap: 8px; align-items: center; }
  .entity-filter-reset {
    background: none;
    border: 1px solid var(--border, #e2e8f0);
    padding: 7px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9em;
  }
  .entity-filter-reset:hover { background: var(--bg, #f7fafc); }
`;

export type FacetLoader = (field: string, filters: EntityFilterInput[], size: number) => Promise<FacetBucket[]>;

export class FilterBar {
  private values = new Map<string, string[]>();
  private facetOptions = new Map<string, FacetBucket[]>();
  private facetRequests = new Map<string, number>();
  private closesPopovers = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly controls: FilterControl[],
    private readonly loadFacets: FacetLoader,
    private readonly onChange: () => void,
    /** Called when facet labels arrive, so a heading built from codes can be redrawn with names. */
    private readonly onLabels: () => void = () => {}
  ) {}

  /** Render the controls with the current values, then load the facets that can load. */
  mount(): void {
    this.container.className = 'entity-filter-bar';
    this.container.innerHTML = this.controls.map(c => this.controlHtml(c)).join('') + `
      <div class="entity-filter-actions">
        <button type="button" class="entity-filter-reset" id="entity-filter-reset">Clear filters</button>
      </div>`;
    this.attach();
    this.refreshFacets();
  }

  /** Read f_<key> parameters. Returns whether any filter is set. */
  readURL(params: URLSearchParams): boolean {
    this.values.clear();
    for (const control of this.controls) {
      const raw = params.get(URL_PREFIX + control.key);
      if (!raw) continue;
      const values = raw.split(URL_SEPARATOR).map(v => v.trim()).filter(Boolean);
      if (values.length) this.values.set(control.key, values);
    }
    return this.values.size > 0;
  }

  /** Write f_<key> parameters into a URL. */
  writeURL(url: URL): void {
    for (const control of this.controls) {
      const values = this.values.get(control.key);
      if (values?.length) url.searchParams.set(URL_PREFIX + control.key, values.join(URL_SEPARATOR));
      else url.searchParams.delete(URL_PREFIX + control.key);
    }
  }

  /** Re-render from the URL (back/forward). */
  syncFromURL(params: URLSearchParams): void {
    this.readURL(params);
    this.mount();
  }

  hasFilters(): boolean {
    return this.toInputs().length > 0;
  }

  /** The API filters for the current values. */
  toInputs(exclude?: string): EntityFilterInput[] {
    const inputs: EntityFilterInput[] = [];
    for (const control of this.controls) {
      if (control.key === exclude || control.type === 'cycle') continue;
      const values = this.values.get(control.key);
      if (!values?.length) continue;
      if (control.type === 'bands') {
        const band = control.bands.find(b => b.id === values[0]);
        if (band && (band.min !== undefined || band.max !== undefined)) {
          inputs.push({ field: control.key, ...(band.min !== undefined && { min: band.min }), ...(band.max !== undefined && { max: band.max }) });
        }
      } else {
        inputs.push({ field: control.key, values });
      }
    }
    return inputs;
  }

  /** Short description of the active filters, for the results heading. */
  describe(): string {
    const parts: string[] = [];
    for (const control of this.controls) {
      const values = this.values.get(control.key);
      if (!values?.length) continue;
      parts.push(values.map(v => this.valueLabel(control, v)).join(' or '));
    }
    return parts.join(' · ');
  }

  private valueLabel(control: FilterControl, value: string): string {
    switch (control.type) {
      case 'select':
      case 'checks':
        return control.options.find(o => o.value === value)?.label ?? value;
      case 'bands':
        return `${control.label}: ${control.bands.find(b => b.id === value)?.label ?? value}`;
      case 'facet': {
        const bucket = this.facetOptions.get(control.key)?.find(b => b.value === value);
        return bucket ? this.facetLabel(control, bucket) : value;
      }
      case 'text':
        return `${control.label}: ${value}`;
      case 'cycle':
        return value;
    }
  }

  private facetLabel(control: Extract<FilterControl, { type: 'facet' }>, bucket: FacetBucket): string {
    return control.formatValue ? control.formatValue(bucket) : (bucket.label || bucket.value);
  }

  private dependenciesMet(control: FilterControl): boolean {
    return control.type !== 'facet' || (control.dependsOn ?? []).every(dep => (this.values.get(dep) ?? []).length > 0);
  }

  private controlHtml(control: FilterControl): string {
    const id = `entity-filter-${control.key}`;
    const values = this.values.get(control.key) ?? [];
    const hidden = this.dependenciesMet(control) ? '' : ' hidden';
    switch (control.type) {
      case 'select':
      case 'bands': {
        const options = control.type === 'select'
          ? control.options.map(o => ({ value: o.value, label: o.label }))
          : control.bands.map(b => ({ value: b.id, label: b.label }));
        return `<div class="entity-filter-control" data-key="${control.key}"${hidden}>
          <label for="${id}">${escapeHtml(control.label)}</label>
          <select id="${id}" data-key="${control.key}">
            <option value="">Any</option>
            ${options.map(o => `<option value="${escapeHtml(o.value)}"${values[0] === o.value ? ' selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
          </select>
        </div>`;
      }
      case 'facet':
        return `<div class="entity-filter-control" data-key="${control.key}"${hidden}>
          <label for="${id}">${escapeHtml(control.label)}</label>
          <select id="${id}" data-key="${control.key}" disabled>
            <option value="">Loading…</option>
          </select>
        </div>`;
      case 'checks':
        return `<div class="entity-filter-control" data-key="${control.key}"${hidden}>
          <span class="entity-filter-label">${escapeHtml(control.label)}</span>
          <details id="${id}">
            <summary>${escapeHtml(this.checksSummary(control, values))}</summary>
            <div class="entity-filter-checks">
              ${control.options.map(o => `<label><input type="checkbox" data-key="${control.key}" value="${escapeHtml(o.value)}"${values.includes(o.value) ? ' checked' : ''}> ${escapeHtml(o.label)}</label>`).join('')}
            </div>
          </details>
        </div>`;
      case 'cycle': {
        const cycle = currentCycle();
        return `<div class="entity-filter-control" data-key="${control.key}">
          <label for="${id}">${escapeHtml(control.label)}</label>
          <select id="${id}" data-cycle-select>
            <option value="">All cycles</option>
            ${control.cycles.map(c => `<option value="${c}"${c === cycle ? ' selected' : ''}>${c - 1}\u2013${c}</option>`).join('')}
          </select>
        </div>`;
      }
      case 'text':
        return `<div class="entity-filter-control" data-key="${control.key}"${hidden}>
          <label for="${id}">${escapeHtml(control.label)}</label>
          <input type="text" id="${id}" data-key="${control.key}" placeholder="${escapeHtml(control.placeholder ?? '')}" value="${escapeHtml(values[0] ?? '')}" autocomplete="off">
        </div>`;
    }
  }

  private checksSummary(control: Extract<FilterControl, { type: 'checks' }>, values: string[]): string {
    if (!values.length) return 'Any';
    if (values.length <= 2) return values.map(v => control.options.find(o => o.value === v)?.label ?? v).join(', ');
    return `${values.length} selected`;
  }

  private attach(): void {
    this.container.querySelector<HTMLSelectElement>('select[data-cycle-select]')?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.location.assign(withCycle(url.href, value ? Number(value) : null));
    });
    this.container.querySelectorAll<HTMLSelectElement>('select[data-key]').forEach(select => {
      select.addEventListener('change', () => this.set(select.dataset.key!, select.value ? [select.value] : []));
    });
    this.container.querySelectorAll<HTMLInputElement>('input[type="text"][data-key]').forEach(input => {
      const apply = () => {
        const value = input.value.trim();
        const current = this.values.get(input.dataset.key!)?.[0] ?? '';
        if (value !== current) this.set(input.dataset.key!, value ? [value] : []);
      };
      input.addEventListener('change', apply);
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          apply();
        }
      });
    });
    this.container.querySelectorAll<HTMLDetailsElement>('details').forEach(details => {
      const key = details.id.replace('entity-filter-', '');
      const control = this.controls.find(c => c.key === key);
      details.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(box => {
        box.addEventListener('change', () => {
          const checked = [...details.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked')].map(b => b.value);
          const summary = details.querySelector('summary');
          if (summary && control?.type === 'checks') summary.textContent = this.checksSummary(control, checked);
          this.set(key, checked);
        });
      });
    });
    if (!this.closesPopovers) {
      // Close an open checkbox list when clicking elsewhere (once per page).
      this.closesPopovers = true;
      document.addEventListener('click', e => {
        this.container.querySelectorAll<HTMLDetailsElement>('details[open]').forEach(details => {
          if (!details.contains(e.target as Node)) details.open = false;
        });
      });
    }
    this.container.querySelector('#entity-filter-reset')?.addEventListener('click', () => {
      this.values.clear();
      this.mount();
      this.onChange();
    });
  }

  private set(key: string, values: string[]): void {
    if (values.length) this.values.set(key, values);
    else this.values.delete(key);

    // Clear and hide controls whose dependencies are no longer met; reload dependent facets.
    for (const control of this.controls) {
      if (control.type !== 'facet' || !(control.dependsOn ?? []).includes(key)) continue;
      this.values.delete(control.key);
      const wrapper = this.container.querySelector<HTMLElement>(`.entity-filter-control[data-key="${control.key}"]`);
      if (wrapper) wrapper.hidden = !this.dependenciesMet(control);
    }
    this.refreshFacets(key);
    this.onChange();
  }

  /** Load facet options; only those depending on changedKey when given. */
  private refreshFacets(changedKey?: string): void {
    for (const control of this.controls) {
      if (control.type !== 'facet') continue;
      const dependsOn = control.dependsOn ?? [];
      if (changedKey !== undefined && !dependsOn.includes(changedKey)) continue;
      const select = this.container.querySelector<HTMLSelectElement>(`select[data-key="${control.key}"]`);
      if (!select) continue;
      if (!this.dependenciesMet(control)) {
        select.innerHTML = '<option value="">Any</option>';
        continue;
      }
      void this.loadFacet(control, select);
    }
  }

  private async loadFacet(control: Extract<FilterControl, { type: 'facet' }>, select: HTMLSelectElement): Promise<void> {
    const request = (this.facetRequests.get(control.key) ?? 0) + 1;
    this.facetRequests.set(control.key, request);
    select.disabled = true;
    select.innerHTML = '<option value="">Loading…</option>';

    const scope = (control.dependsOn ?? []).flatMap(dep => this.toInputs().filter(f => f.field === dep));
    let buckets: FacetBucket[] = [];
    try {
      buckets = await this.loadFacets(control.key, scope, control.size ?? 200);
    } catch {
      buckets = [];
    }
    if (this.facetRequests.get(control.key) !== request) return; // superseded

    if (control.sortByLabel !== false) {
      buckets = [...buckets].sort((a, b) => this.facetLabel(control, a).localeCompare(this.facetLabel(control, b), 'en', { numeric: true }));
    }
    this.facetOptions.set(control.key, buckets);

    const wrapper = select.closest<HTMLElement>('.entity-filter-control');
    if (!buckets.length) {
      // No values (e.g. a field the index does not carry yet): hide the control.
      if (wrapper) wrapper.hidden = true;
      return;
    }
    if (wrapper) wrapper.hidden = false;
    const selected = this.values.get(control.key)?.[0] ?? '';
    if (selected) this.onLabels();
    select.innerHTML = '<option value="">Any</option>' + buckets.map(b =>
      `<option value="${escapeHtml(b.value)}"${b.value === selected ? ' selected' : ''}>${escapeHtml(this.facetLabel(control, b))} (${b.count.toLocaleString()})</option>`
    ).join('');
    select.disabled = false;
  }
}

/** Bands helper: "$1K+"-style minimum bands. */
export function minimumBands(entries: Array<[string, number]>): FilterBand[] {
  return entries.map(([label, min]) => ({ id: String(min), label, min }));
}
